import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
	projectSchema,
	projectFileValidations,
} from '../shared/js/validations.js';

import '../shared/sass/CreateAndUpdateShared.scss';
import { Loader } from '../../components/Loader/Loader.jsx';
import { Logout } from '../../components/Logout/Logout.jsx';
import { API_URL } from '../../config.js';
import { TextEditor } from '../../components/TextEditor/TextEditor.jsx';
import { ModalMessage } from '../../components/ModalMessage/ModalMessage.jsx';
import { BackButton } from '../../components/BackButton/BackButton.jsx';

export const CreateAndUpdateProject = () => {
	const { id } = useParams();
	const isEditMode = Boolean(id);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);

	const [formData, setFormData] = useState({
		name: '',
		title: '',
		description: '',
		cover: null,
		image: null,
		image_2: null,
		video: null,
		audio: null,
		external_video: '',
		external_link: '',
		external_link_name: '',
		bandcamp: '',
	});
	const [description, setDescription] = useState('');
	const [fieldsToDelete, setFieldsToDelete] = useState([]);

	useEffect(() => {
		if (isEditMode) {
			const getProject = async () => {
				try {
					setLoading(true);
					const { data: response } = await axios.get(
						`${API_URL}/projects/${id}`
					);
					setFormData({
						name: response.data.name,
						title: response.data.title,
						description: response.data.description,
						cover: response.data.cover,
						image: response.data.image,
						image_2: response.data.image_2,
						video: response.data.video,
						audio: response.data.audio,
						external_video: response.data.external_video,
						external_link: response.data.external_link,
						external_link_name: response.data.external_link_name,
						bandcamp: response.data.bandcamp,
					});
					setDescription(response.data.description);
				} catch (error) {
					console.log(error);
					setError(error.message);
				} finally {
					setLoading(false);
				}
			};
			getProject();
		}
	}, [id, isEditMode]);

	const handleChange = (event) => {
		const { name, value, files } = event.target;
		setFormData((prevState) => ({
			...prevState,
			[name]: files ? files[0] : value,
		}));
	};

	const toggleFieldDeletion = (field) => {
		setFieldsToDelete((prev) => {
			if (prev.includes(field)) {
				return prev.filter((f) => f !== field);
			} else {
				// reset value on delete
				setFormData((prevData) => ({
					...prevData,
					[field]: '',
				}));
				return [...prev, field];
			}
		});
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setLoading(true);
		setSuccess(null);
		setError(null);

		try {
			// Validación Yup de campos de texto
			await projectSchema.validate(
				{ ...formData, description },
				{ abortEarly: false }
			);

			// Validación personalizada de archivos
			const fileErrors = projectFileValidations(formData, isEditMode);
			if (fileErrors.length > 0) {
				setError(fileErrors.join('\n'));
				setLoading(false);
				return;
			}

			// Preparar formData
			const formDataToSend = new FormData();
			Object.entries(formData).forEach(([key, value]) => {
				if (value !== null && !fieldsToDelete.includes(key)) {
					formDataToSend.append(key, value);
				}
			});
			formDataToSend.set('description', description);

			if (isEditMode && fieldsToDelete.length > 0) {
				formDataToSend.append(
					'fieldsToDelete',
					JSON.stringify(fieldsToDelete)
				);
			}

			const method = isEditMode ? 'patch' : 'post';
			const url = isEditMode
				? `${API_URL}/projects/${id}`
				: `${API_URL}/projects`;

			await axios[method](url, formDataToSend, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});

			setSuccess(
				`Proyecto ${isEditMode ? 'actualizado' : 'creado'} con éxito`
			);
			if (!isEditMode) {
				setFormData({
					name: '',
					title: '',
					description: '',
					cover: null,
					image: null,
					image_2: null,
					video: null,
					audio: null,
					external_video: '',
					external_link: '',
					external_link_name: '',
					bandcamp: '',
				});
				setDescription('');
				setFieldsToDelete([]);
			}
		} catch (err) {
			if (err.name === 'ValidationError') {
				setError(err.errors.join('\n'));
			} else if (
				err.response?.data?.errors &&
				Array.isArray(err.response.data.errors)
			) {
				const messages = err.response.data.errors
					.map((e) => e.msg)
					.join('\n');
				setError(messages);
			} else {
				setError(err.response?.data?.message || err.message);
			}
		} finally {
			setLoading(false);
		}
	};

	const renderDeleteCheckbox = (field) =>
		isEditMode &&
		field !== 'cover' && (
			<div className='delete-checkbox'>
				<input
					type='checkbox'
					id={`delete-${field}`}
					checked={fieldsToDelete.includes(field)}
					onChange={() => toggleFieldDeletion(field)}
				/>
				<label htmlFor={`delete-${field}`}>x</label>
			</div>
		);

	return (
		<div className='CreateAndUpdateShared'>
			{loading && <Loader />}
			<div className='container'>
				{isEditMode ? (
					<BackButton to='/list-projects' text='Lista de Proyectos' />
				) : (
					<BackButton to='/dashboard' text='Dashboard' />
				)}

				<form className='CreateAndUpdate-form' onSubmit={handleSubmit}>
					{error && (
						<ModalMessage
							type='error'
							message={error}
							onClose={() => setError(null)}
						/>
					)}
					{success && (
						<ModalMessage
							type='success'
							message={success}
							onClose={() => setSuccess(null)}
						/>
					)}
					<Logout />
					<h3>{isEditMode ? 'EDITAR PROYECTO' : 'CREAR PROYECTO'}</h3>

					<label>Nombre:</label>
					<input
						type='text'
						name='name'
						onChange={handleChange}
						value={formData.name}
					/>

					<label>Título:</label>
					<div className='input-container'>
						<input
							type='text'
							name='title'
							onChange={handleChange}
							value={formData.title}
							disabled={fieldsToDelete.includes('title')}
						/>
						{renderDeleteCheckbox('title')}
					</div>

					<label>Descripción:</label>
					<TextEditor
						editorState={description}
						setEditorState={setDescription}
					/>

					<label>Portada:</label>
					<div className='input-container'>
						<input
							type='file'
							name='cover'
							onChange={handleChange}
							accept='image/*'
						/>
						{formData.cover?.secure_url && (
							<img src={formData.cover.secure_url} alt='cover' />
						)}
					</div>

					<label>Imagen 1:</label>
					<div className='input-container'>
						<input
							type='file'
							name='image'
							onChange={handleChange}
							accept='image/*'
							disabled={fieldsToDelete.includes('image')}
						/>
						{formData.image?.secure_url && (
							<img
								src={formData.image.secure_url}
								alt='image_1'
							/>
						)}
						{renderDeleteCheckbox('image')}
					</div>

					{/* IMAGE_2 */}
					<label>Imagen 2:</label>
					<div className='input-container'>
						<input
							type='file'
							name='image_2'
							onChange={handleChange}
							accept='image/*'
							disabled={fieldsToDelete.includes('image_2')}
						/>
						{formData.image_2?.secure_url && (
							<img
								src={formData.image_2.secure_url}
								alt='image_2'
							/>
						)}
						{renderDeleteCheckbox('image_2')}
					</div>

					<label>Vídeo:</label>
					<div className='input-container'>
						<input
							type='file'
							name='video'
							onChange={handleChange}
							accept='video/*'
							disabled={fieldsToDelete.includes('video')}
						/>
						{formData.video?.secure_url && (
							<video src={formData.video.secure_url} controls />
						)}
						{renderDeleteCheckbox('video')}
					</div>

					<label>Audio:</label>
					<div className='input-container'>
						<input
							type='file'
							name='audio'
							onChange={handleChange}
							accept='audio/*'
							disabled={fieldsToDelete.includes('audio')}
						/>
						{formData.audio?.secure_url && (
							<audio src={formData.audio.secure_url} controls />
						)}
						{renderDeleteCheckbox('audio')}
					</div>

					<label>Vídeo Externo:</label>
					<div className='input-container'>
						<input
							type='text'
							name='external_video'
							onChange={handleChange}
							value={formData.external_video}
							disabled={fieldsToDelete.includes('external_video')}
						/>
						{renderDeleteCheckbox('external_video')}
					</div>

					<label>Enlace externo:</label>
					<div className='input-container'>
						<input
							type='text'
							name='external_link'
							onChange={handleChange}
							value={formData.external_link}
							disabled={fieldsToDelete.includes('external_link')}
						/>
						{renderDeleteCheckbox('external_link')}
					</div>

					<label>Nombre del enlace externo:</label>
					<div className='input-container'>
						<input
							type='text'
							name='external_link_name'
							onChange={handleChange}
							value={formData.external_link_name}
							disabled={fieldsToDelete.includes(
								'external_link_name'
							)}
						/>
						{renderDeleteCheckbox('external_link_name')}
					</div>
					<label>Bandcamp:</label>
					<div className='input-container'>
						<input
							type='text'
							name='bandcamp'
							onChange={handleChange}
							value={formData.bandcamp}
							disabled={fieldsToDelete.includes('bandcamp')}
						/>
						{renderDeleteCheckbox('bandcamp')}
					</div>

					<button className='submit-btn' type='submit'>
						{isEditMode ? 'Actualizar' : 'Crear'} Proyecto
					</button>
				</form>
			</div>
		</div>
	);
};
